import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type { ExamBalancePlan, ExamSession, ExamType } from '@/types/exam';
import { instituteToday } from '../utils';
import { EXAM_BALANCE_PLANS, EXAM_TYPES } from './schema';

/**
 * How the console describes a paper.
 *
 * One source of truth for every label and tone, so "Unit Test" reads the same
 * on the card, in the filter row and on the marks sheet (CLAUDE.md §3).
 */

export const EXAM_TYPE_LABEL = {
  UNIT_TEST: 'Unit test',
  MID_TERM: 'Mid-term',
  FINAL: 'Final',
  PRACTICAL: 'Practical',
  ORAL: 'Oral',
  ASSIGNMENT: 'Assignment',
  FORMATIVE: 'Formative',
  SUMMATIVE: 'Summative',
} as const satisfies Record<ExamType, string>;

export const EXAM_TYPE_HINT = {
  UNIT_TEST: 'A short paper through the term.',
  MID_TERM: 'Half-yearly.',
  FINAL: 'The annual paper.',
  PRACTICAL: 'Marked at the bench — lab work, not a written paper.',
  ORAL: 'Spoken: reading, recitation or viva.',
  ASSIGNMENT: 'A marked piece of work that counts towards the term.',
  FORMATIVE: 'Continuous assessment through the term.',
  SUMMATIVE: 'The term-end paper.',
} as const satisfies Record<ExamType, string>;

/**
 * The bigger the paper, the heavier the tone — a final carries more than a
 * unit test and the card should say so at a glance.
 */
export const EXAM_TYPE_TONE = {
  UNIT_TEST: 'neutral',
  MID_TERM: 'info',
  FINAL: 'accent',
  PRACTICAL: 'success',
  ORAL: 'warning',
  ASSIGNMENT: 'neutral',
  FORMATIVE: 'info',
  SUMMATIVE: 'accent',
} as const satisfies Record<ExamType, BadgeTone>;

export const EXAM_TYPE_OPTIONS: Option<ExamType>[] = EXAM_TYPES.map((value) => ({
  value,
  label: EXAM_TYPE_LABEL[value],
}));

/**
 * The kinds the office sets an exam as. Practicals, orals and assignments are
 * class work a teacher sets, not a paper on the examination timetable.
 */
export const INSTITUTE_EXAM_TYPES = [
  'FORMATIVE',
  'SUMMATIVE',
  'UNIT_TEST',
  'MID_TERM',
  'FINAL',
] as const satisfies readonly ExamType[];

/**
 * The office's choices — plus the paper's own kind when it is not one of them,
 * so editing a paper set before the list narrowed does not quietly change it.
 */
export function instituteExamTypeOptions(current?: ExamType): Option<ExamType>[] {
  const kinds: readonly ExamType[] =
    current && !(INSTITUTE_EXAM_TYPES as readonly ExamType[]).includes(current)
      ? [...INSTITUTE_EXAM_TYPES, current]
      : INSTITUTE_EXAM_TYPES;

  return kinds.map((value) => ({ value, label: EXAM_TYPE_LABEL[value] }));
}

export const EXAM_TYPE_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'All kinds' },
  ...EXAM_TYPE_OPTIONS,
];

/**
 * Which half of the day a paper sits in, as an Indian institute says it.
 *
 * The value itself is the server's — read off the paper's start time — so this
 * is only how it is printed. One place, so the exam table and the two exam
 * forms cannot describe the same paper differently (CLAUDE.md §3).
 */
export const EXAM_SESSION_LABEL = {
  MORNING: 'Morning session',
  AFTERNOON: 'Afternoon session',
} as const satisfies Record<ExamSession, string>;

/** What the rest of the exam day holds, as it reads to a parent. */
export const EXAM_BALANCE_PLAN_LABEL = {
  PREPARATION_LEAVE: 'Study hours',
  SECOND_EXAM: 'Second paper',
  REGULAR_CLASSES: 'Regular classes',
  EARLY_DISPERSAL: 'Early dispersal',
} as const satisfies Record<ExamBalancePlan, string>;

/** The longer wording, for the form where the office is choosing between them. */
export const EXAM_BALANCE_PLAN_HINT = {
  PREPARATION_LEAVE: 'Study hours for the rest of the day',
  SECOND_EXAM: 'A second paper in the other half of the day',
  REGULAR_CLASSES: 'Regular classes in the remaining periods',
  EARLY_DISPERSAL: 'School closes once the paper is collected',
} as const satisfies Record<ExamBalancePlan, string>;

export const EXAM_BALANCE_PLAN_OPTIONS: Option<ExamBalancePlan>[] = EXAM_BALANCE_PLANS.map(
  (value) => ({ value, label: EXAM_BALANCE_PLAN_HINT[value] }),
);

/** Rows the sheet can be narrowed to while entering marks. */
export const MARK_FILTER_OPTIONS: Option[] = [
  { value: '', label: 'Everyone' },
  { value: 'PENDING', label: 'Not entered' },
  { value: 'ENTERED', label: 'Entered' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'FAILED', label: 'Below the pass mark' },
];

/**
 * The earliest day an exam date picker offers — today, so no paper is set for
 * a day that has passed. Editing a paper already sat keeps its own date
 * selectable, so correcting its name or total never forces a new date; the
 * server refuses a past date only when it is new (`assertNotInPast`).
 */
export function earliestExamDate(currentDate?: string | null): string {
  const today = instituteToday();
  return currentDate && currentDate < today ? currentDate : today;
}
